import React, { useCallback, useEffect, useRef, useState } from 'react';
import { css } from 'emotion';
import { useDispatch, useSelector } from 'react-redux';
import { Formik } from 'formik';
import { format, parseISO } from 'date-fns';
import {
  Badge,
  Box,
  Button,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  Heading,
  Icon,
  IconButton,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Text,
  Textarea,
  Tooltip,
  useDisclosure,
} from '@chakra-ui/core';
import { apiV1 } from '../../api/api';
import { Section } from '../Sections';
import { TabTableWrap, TabTBodyRow, TabTBodyTH, TabTD } from '../Tabs';
import { Table } from '../Table';
import QuoteUpload from '../QuoteUpload/QuoteUpload';
import { QuoteDocumentsAction } from '../QuoteDocumentsAction';
import Comments from '../Comments/Comments';
import Can from '../../../roleBasedAccess/Can';
import { toastActions } from '../../../redux/toast/actions';
import { TOAST_MESSAGE } from '../Toasts/Toast';
import { DateInput } from '../Form/FormDateInput';
import {
  QuoteProgressTimeline,
  QUOTE_PROGRESS_STAGES,
} from '../../../quotes/QuoteProgressTimeline';
import { CompanyDocumentsPanel } from '../CompanyDocuments/CompanyDocumentsPanel';
import { FundingDocumentsReviewPanel } from '../../../folders/FolderEdit/blocs/BlocC/FundingDocumentsReviewPanel';
import { getCompanyDocumentsCompletion } from '../CompanyDocuments/documentCompletion';
import { getFundingDocumentsCompletion } from './documentCompletion';
import DocumentSectionButton from './DocumentSectionButton';
import { FundingDocumentsList } from './FundingDocumentsList';
import {
  uploadFundingDocumentFiles,
  uploadProgressMessage,
  uploadSummaryMessage,
} from './fundingDocumentUpload';
import ActionAge from '../ActionAge';
import { quoteReference } from '../../helpers/quoteReference';
import QuoteQuestionDetails from '../../../quotes/QuoteQuestionDetails';
import { canAskQuestions, questionState } from '../../../quotes/questionHelpers';
import { getUploadError } from '../../helpers/fileUpload';

const displayValue = value => {
  if (value === true) return 'Oui';
  if (value === false) return 'Non';
  return value === null || value === undefined || value === '' ? '-' : value;
};

const QUOTE_ACTION_LABELS = ['Devis initial à déposer', 'Devis modifié à déposer'];

const DISCARDED_DISABLED_STAGES = [
  QUOTE_PROGRESS_STAGES.funding,
  QUOTE_PROGRESS_STAGES.projectFollowUp,
];

const worksAmount = value => {
  if (typeof value !== 'string') return value;

  return value.replace(/^(?:Montant|Coût) des travaux\s*(?::|=)\s*/i, '');
};

const projectDateValue = value => {
  if (!value) return null;

  const date = parseISO(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const displayProjectDate = value => {
  const date = projectDateValue(value);
  return date ? format(date, 'dd/MM/yyyy') : '';
};

const displayStatusAge = days => {
  if (days === null || days === undefined) return 'Non connue';
  return `${days} jour${days > 1 ? 's' : ''}`;
};

const stageMetadata = (folder, quote) => {
  let projectValidation = null;
  if (quote?.lastQuoteValidatedAt) {
    projectValidation = { label: 'Devis validés', value: quote.lastQuoteValidatedAt };
  } else if (quote?.lastQuoteReceivedAt) {
    projectValidation = { label: 'Dernier devis', value: quote.lastQuoteReceivedAt };
  }

  let projectFollowUp = null;
  if (quote?.projectEndedAt) {
    projectFollowUp = {
      label: 'Fin du projet',
      value: displayProjectDate(quote.projectEndedAt),
    };
  } else if (quote?.projectStartedAt) {
    projectFollowUp = {
      label: 'Début du projet',
      value: displayProjectDate(quote.projectStartedAt),
    };
  }

  return {
    [QUOTE_PROGRESS_STAGES.diagnostic]: folder.housingVisitedAt
      ? { label: 'Visite à domicile', value: folder.housingVisitedAt }
      : null,
    [QUOTE_PROGRESS_STAGES.projectValidation]: projectValidation,
    [QUOTE_PROGRESS_STAGES.funding]: folder.financialInvestigationEndDate
      ? { label: 'Fin d’instruction', value: folder.financialInvestigationEndDate }
      : null,
    [QUOTE_PROGRESS_STAGES.projectFollowUp]: projectFollowUp,
  };
};

const quoteCurrentState = (quote, analysisFinishedAt) => {
  const status = quote?.lastQuoteVersion?.status;
  if (quote?.indicator === 'Devis non reçu') return 'En attente du devis';
  if (quote?.indicator === 'Demande de modifications') return '🚫 Modification demandée';
  if (status === 'valid' && analysisFinishedAt) return '✔️ Devis validé';
  if (status === 'valid') return '✔️ Devis validé';
  if (status === 'additional_request') return '🚫 Modification demandée';
  if (!quote?.lastQuoteVersion?.filename) return 'En attente du devis';
  return 'Analyse en cours';
};

const DetailGrid = ({ children }) => (
  <Box
    display="grid"
    gridTemplateColumns={{ base: '1fr', md: 'repeat(2, minmax(0, 1fr))' }}
    gridGap={3}
  >
    {children}
  </Box>
);

const DetailRow = ({ label, value, action }) => (
  <Flex
    px={4}
    py={3}
    bg="lightGray"
    rounded="md"
    alignItems="center"
    justifyContent="space-between"
    gridGap={3}
    minW={0}
  >
    <Box minW={0}>
      <Text fontSize="xs" color="semiDarkGray.600" mb={1}>
        {label}
      </Text>
      <Text fontSize="sm" color="semiDarkGray.900" fontWeight="semibold" overflowWrap="anywhere">
        {displayValue(value)}
      </Text>
    </Box>
    {action}
  </Flex>
);

const FollowUpDetails = ({ folder, quote }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const contentId = `quote-follow-up-${quote.id || folder.id}`;

  return (
    <Box borderTop="1px solid" borderColor="lightGray3">
      <Flex
        as="button"
        type="button"
        width="100%"
        px={4}
        py={3}
        alignItems="center"
        textAlign="left"
        cursor="pointer"
        fontSize="sm"
        fontWeight="semibold"
        color="semiDarkGray.900"
        aria-expanded={isExpanded}
        aria-controls={contentId}
        onClick={() => setIsExpanded(expanded => !expanded)}
        _hover={{ bg: 'lightGray' }}
        _focus={{
          outline: '2px solid',
          outlineColor: 'coral.700',
          outlineOffset: '-2px',
        }}
      >
        <Icon
          name="chevron-right"
          aria-hidden="true"
          mr={1}
          flexShrink={0}
          transform={isExpanded ? 'rotate(90deg)' : 'rotate(0deg)'}
          className={css({
            transition: 'transform 260ms ease',
            '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
          })}
        />
        Repères de suivi
      </Flex>
      <Box
        id={contentId}
        role="region"
        aria-label="Repères de suivi"
        aria-hidden={!isExpanded}
        display="grid"
        gridTemplateRows={isExpanded ? '1fr' : '0fr'}
        opacity={isExpanded ? 1 : 0}
        visibility={isExpanded ? 'visible' : 'hidden'}
        className={css({
          transition: `grid-template-rows 260ms ease, opacity 200ms ease, visibility 0s ${
            isExpanded ? '0s' : '260ms'
          }`,
          '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
        })}
      >
        <Box minH={0} overflow="hidden">
          <Box px={4} pb={4}>
            <DetailGrid>
              <DetailRow label="Conseiller·ère social" value={folder.socialAdviser} />
              <DetailRow
                label="Contrat envoyé pour signature"
                value={quote.contractSentForSignatureAt}
              />
              <DetailRow label="Date de paiement" value={quote.paymentAt} />
            </DetailGrid>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

const QUOTE_TICKET_STATUS = {
  open: {
    background: 'tag.bg.red',
    color: 'red',
    label: 'En attente de réponse',
    icon: 'comment',
  },
  processed: {
    background: 'lightGray3',
    color: 'semiDarkGray.900',
    label: 'Traitée',
    icon: 'check',
  },
  other: {
    background: 'lightGray',
    color: 'semiDarkGray.600',
    label: 'Échange antérieur',
    icon: 'info-outline',
  },
};

const quoteTicketDate = ticket => ticket.createdAt || ticket.created_at || '-';

const parseTicketDate = value => {
  if (!value) return 0;

  const parsed = new Date(value);
  if (!Number.isNaN(parsed.getTime())) return parsed.getTime();

  const frenchDate = String(value).match(/^(\d{2})\/(\d{2})\/(\d{4})(?: à | )?(\d{2})?:?(\d{2})?$/);
  if (!frenchDate) return 0;

  const [, day, month, year, hours = '00', minutes = '00'] = frenchDate;
  return new Date(`${year}-${month}-${day}T${hours}:${minutes}:00`).getTime();
};

const sortQuoteTickets = tickets =>
  [...tickets].sort((first, second) => {
    const dateDifference =
      parseTicketDate(quoteTicketDate(second)) - parseTicketDate(quoteTicketDate(first));
    if (dateDifference !== 0) return dateDifference;

    return Number(second.id || 0) - Number(first.id || 0);
  });

const extractQuoteTickets = data => {
  if (Array.isArray(data)) return data;
  const tickets = data?.quoteTickets || data?.quote_tickets || [];
  return Array.isArray(tickets) ? tickets : [];
};

const QuoteTicketStatus = ({ ticket }) => {
  const statusDetails = QUOTE_TICKET_STATUS[questionState(ticket)];
  const answered = Boolean(ticket.response?.trim());
  const label = answered ? 'Voir la réponse' : statusDetails.label;

  return (
    <Badge
      as="span"
      id={`quote-history-status-${ticket.id}`}
      aria-label={label}
      data-status={questionState(ticket)}
      display="inline-flex"
      alignItems="center"
      flexShrink={0}
      gridGap={1}
      title={label}
      variantColor={null}
      bg={answered ? 'coral.700' : statusDetails.background}
      color={answered ? 'white' : statusDetails.color}
      px={answered ? 3 : 2}
      py={1}
      rounded={answered ? 'md' : 'full'}
      fontSize="xs"
      fontWeight="semibold"
      letterSpacing="normal"
      textTransform="none"
    >
      {!answered && <Icon name={statusDetails.icon} size="11px" aria-hidden="true" />}
      {label}
      {answered && <Icon name="arrow-forward" size="11px" aria-hidden="true" />}
    </Badge>
  );
};

const QuoteHistoryView = ({
  folderFormattedId,
  historyPending,
  historyError,
  quoteTickets,
  ticketQuestion,
  ticketPending,
  ticketError,
  canAskQuestion,
  onBack,
  backButtonRef,
  onSelectTicket,
  onQuestionChange,
  onSubmit,
}) => (
  <>
    <ModalHeader bg="darkGray" color="white" roundedTop="lg" pr={12} order={-1}>
      <Button
        type="button"
        variant="ghost"
        variantColor={null}
        color="white"
        size="sm"
        px={0}
        mb={3}
        leftIcon="arrow-back"
        ref={backButtonRef}
        onClick={onBack}
        _hover={{ bg: 'transparent', color: 'coral.500' }}
        _focus={{ shadow: 'outline' }}
      >
        Retour au dossier
      </Button>
      <Heading as="h2" color="white" fontSize="xl">
        Historique des échanges
      </Heading>
      <Text mt={1} fontSize="sm" fontWeight="normal" opacity={0.8}>
        Dossier n° {folderFormattedId || '-'}
      </Text>
    </ModalHeader>
    <ModalCloseButton color="white" _focus={{ shadow: 'none' }} />
    <ModalBody pb={6} pt={5}>
      <Box role="region" aria-label="Historique de suivi du devis">
        {historyPending && <Spinner aria-label="Chargement des questions" />}
        {!historyPending && historyError && (
          <Text color="semiDarkGray.600">Questions indisponibles pour ce dossier.</Text>
        )}
        {!historyPending && !historyError && quoteTickets?.length === 0 && (
          <Text color="semiDarkGray.600">Aucune question posée.</Text>
        )}
        {!historyPending && !historyError && quoteTickets?.length > 0 && (
          <Box
            maxH="24rem"
            overflowY="auto"
            border="1px solid"
            borderColor="lightGray3"
            rounded="md"
          >
            {quoteTickets.map(ticket => (
              <Button
                key={ticket.id || `${quoteTicketDate(ticket)}-${ticket.question}`}
                type="button"
                variant="ghost"
                variantColor={null}
                width="100%"
                height="auto"
                display="block"
                py={3}
                px={4}
                rounded={0}
                borderBottom="1px solid"
                borderColor="lightGray3"
                textAlign="left"
                whiteSpace="normal"
                title={ticket.question}
                aria-label={`Lire la question : ${ticket.question}`}
                aria-describedby={`quote-history-status-${ticket.id}`}
                onClick={event => onSelectTicket(ticket, event.currentTarget)}
                _hover={{ bg: 'lightGray' }}
                _focus={{ bg: 'lightGray', shadow: 'outline' }}
                _last={{ borderBottom: 0 }}
              >
                <Flex
                  alignItems={{ base: 'flex-start', sm: 'center' }}
                  flexDirection={{ base: 'column', sm: 'row' }}
                  justifyContent="space-between"
                  gridGap={2}
                >
                  <Text fontSize="xs" color="semiDarkGray.600" fontWeight="normal">
                    Soumise le {quoteTicketDate(ticket)}
                  </Text>
                  <QuoteTicketStatus ticket={ticket} />
                </Flex>
                <Text mt={2} noOfLines={2} color="semiDarkGray.900" fontWeight="semibold">
                  {ticket.question}
                </Text>
              </Button>
            ))}
          </Box>
        )}
        {canAskQuestion && (
          <Flex
            as="form"
            mt={4}
            onSubmit={onSubmit}
            alignItems="stretch"
            flexDirection={{ base: 'column', sm: 'row' }}
            gridGap={2}
          >
            <Input
              value={ticketQuestion}
              onChange={onQuestionChange}
              placeholder="poser une question"
              aria-label="poser une question"
              size="sm"
              flex={1}
              minWidth={0}
              minHeight="3rem"
              px={4}
              bg="lightGray"
              borderColor="lightGray3"
              rounded="lg"
              color="semiDarkGray.900"
              focusBorderColor="semiDarkGray.900"
              _placeholder={{ color: 'semiDarkGray.600' }}
              _hover={{ borderColor: 'semiDarkGray.600' }}
              isDisabled={ticketPending}
            />
            <Button
              type="submit"
              variantColor="neutral"
              size="sm"
              height="3rem"
              px={5}
              rounded="lg"
              flexShrink={0}
              rightIcon="arrow-forward"
              aria-label="Poser une question"
              bg="semiDarkGray.900"
              color="white"
              _hover={{ bg: 'darkGray' }}
              _active={{ bg: 'darkGray' }}
              _focus={{ boxShadow: '0 0 0 3px rgba(54, 61, 68, 0.2)' }}
              _disabled={{
                bg: 'lightGray3',
                color: 'semiDarkGray.600',
                opacity: 1,
                cursor: 'not-allowed',
              }}
              isLoading={ticketPending}
              isDisabled={!ticketQuestion.trim()}
            >
              Envoyer
            </Button>
          </Flex>
        )}
        {ticketError && (
          <Text mt={2} fontSize="sm" color="red.500">
            La question n’a pas pu être envoyée.
          </Text>
        )}
      </Box>
    </ModalBody>
  </>
);

const DocumentsDrawer = ({
  isOpen,
  onClose,
  title,
  description,
  returnFocusOnClose = true,
  children,
}) => (
  <Drawer
    isOpen={isOpen}
    placement="right"
    onClose={onClose}
    size="md"
    returnFocusOnClose={returnFocusOnClose}
  >
    <DrawerOverlay bg="rgba(20, 26, 32, 0.58)" data-testid="documents-drawer-overlay" />
    <DrawerContent
      maxW={{ base: '100%', md: '32rem' }}
      maxH="100%"
      overflow="hidden"
      bg="white"
      borderLeft="1px solid"
      borderColor="semiDarkGray.600"
      boxShadow="-22px 0 52px rgba(20, 26, 32, 0.34), -4px 0 14px rgba(35, 40, 45, 0.18)"
      data-testid="documents-drawer-panel"
    >
      <DrawerCloseButton color="white" />
      <DrawerHeader
        bg="darkGray"
        color="white"
        borderBottomWidth="1px"
        boxShadow="0 4px 14px rgba(35, 40, 45, 0.2)"
        pr={12}
        flexShrink={0}
      >
        <Heading as="h3" color="white" fontSize="lg">
          {title}
        </Heading>
      </DrawerHeader>
      <DrawerBody
        p={{ base: 3, md: 4 }}
        minH={0}
        overflowY="auto"
        data-testid="documents-drawer-body"
      >
        {description && (
          <Text color="semiDarkGray.600" fontSize="sm" mb={4}>
            {description}
          </Text>
        )}
        {children}
      </DrawerBody>
    </DrawerContent>
  </Drawer>
);

const MissingDocumentsNotice = ({ labels }) =>
  labels?.length ? (
    <Box
      mb={4}
      p={3}
      rounded="lg"
      border="2px solid"
      borderColor="coral.700"
      bg="#FFF5F5"
      data-testid="missing-documents"
    >
      <Text fontWeight="semibold" mb={1}>
        À déposer
      </Text>
      <Box as="ul" pl={5} fontSize="sm">
        {labels.map(label => (
          <Box as="li" key={label}>
            {label}
          </Box>
        ))}
      </Box>
    </Box>
  ) : null;

const missingFundingDocumentLabels = documents =>
  documents
    .filter(
      document =>
        document.required !== false &&
        ['missing', 'incorrect'].includes(document.state || document.status || 'missing'),
    )
    .map(document => document.name || document.key || document.documentKey);

const FundingDocumentsDrawer = ({
  isOpen,
  onClose,
  actionDetail,
  returnFocusOnClose,
  ...props
}) => (
  <DocumentsDrawer
    isOpen={isOpen}
    onClose={onClose}
    title="Documents administratifs"
    returnFocusOnClose={returnFocusOnClose}
  >
    <Box mb={3}>
      <ActionAge detail={actionDetail} showDate />
    </Box>
    {actionDetail && !props.pending && (
      <MissingDocumentsNotice labels={missingFundingDocumentLabels(props.documents || [])} />
    )}
    <FundingDocumentsList {...props} />
  </DocumentsDrawer>
);

const CompanyDocumentsDrawer = ({
  isOpen,
  onClose,
  quoteId,
  canWrite,
  canFillDischargeLetter,
  quoteUpload,
  onCompletionChange,
  actionDetail,
  folderId,
  canUploadSharedDocuments,
  returnFocusOnClose,
}) => (
  <DocumentsDrawer
    isOpen={isOpen}
    onClose={onClose}
    title="Documents de l’entreprise"
    returnFocusOnClose={returnFocusOnClose}
  >
    <Box mb={3}>
      <ActionAge detail={actionDetail} showDate />
    </Box>
    <MissingDocumentsNotice labels={actionDetail?.documents} />
    <CompanyDocumentsPanel
      companiesQuoteGroupId={quoteId}
      canWrite={canWrite}
      canFillDischargeLetter={canFillDischargeLetter}
      quoteUpload={quoteUpload}
      showHeader={false}
      onCompletionChange={onCompletionChange}
      sharedDocuments={
        folderId ? (
          <FundingDocumentsReviewPanel
            folderId={folderId}
            section="financing"
            plain
            access={{ canUpload: canUploadSharedDocuments }}
            title="Pièces ANAH"
            intro="Ces pièces sont communes à toutes les entreprises du dossier : un seul dépôt suffit."
          />
        ) : null
      }
    />
  </DocumentsDrawer>
);

const noticeDate = value => {
  const date = value ? parseISO(value) : null;
  return date && !Number.isNaN(date.getTime()) ? format(date, 'dd/MM/yyyy') : null;
};

const QuoteNotice = React.forwardRef(
  ({ title, dateLine, highlighted, testId, download, quoteUpload, children }, ref) => (
    <Box
      ref={ref}
      tabIndex={-1}
      mb={5}
      p={4}
      rounded="lg"
      border="2px solid"
      borderColor={highlighted ? 'coral.700' : 'lightGray3'}
      bg={highlighted ? '#FFF5F5' : 'white'}
      data-testid={testId}
    >
      <Heading as="h3" fontSize="md" mb={2}>
        {title}
      </Heading>
      {dateLine && (
        <Text fontSize="sm" color="semiDarkGray.600" mb={2}>
          {dateLine}
        </Text>
      )}
      {children}
      {(download || quoteUpload) && (
        <Flex mt={4} flexWrap="wrap" gridGap={3}>
          {download && (
            <Button
              as="a"
              href={download.href}
              target="_blank"
              rel="noopener noreferrer"
              size="sm"
              variant="outline"
              variantColor="neutral"
              leftIcon="view"
              borderColor="coral.900"
              color="coral.900"
              _hover={{ bg: 'coral.900', color: 'white' }}
              _focus={{ shadow: 'none' }}
            >
              {download.label}
            </Button>
          )}
          {quoteUpload}
        </Flex>
      )}
    </Box>
  ),
);

export const ModificationRequestNotice = React.forwardRef(
  ({ request, highlighted, quoteUpload }, ref) => {
    const requestedOn = noticeDate(request.requestedOn);
    let dateLine = null;
    if (requestedOn)
      dateLine = request.comment
        ? `Demandées le ${requestedOn}`
        : `Modification demandée le ${requestedOn}`;

    return (
      <QuoteNotice
        ref={ref}
        testId="modification-request"
        title={`Modifications demandées${
          request.quoteNumber ? ` sur le devis ${request.quoteNumber}` : ''
        }`}
        dateLine={dateLine}
        highlighted={highlighted}
        download={
          request.downloadLink && {
            href: request.downloadLink,
            label: request.quoteNumber ? `Voir le devis ${request.quoteNumber}` : 'Voir le devis',
          }
        }
        quoteUpload={quoteUpload}
      >
        {request.comment ? (
          <Text whiteSpace="pre-line">{request.comment}</Text>
        ) : (
          <Text color="semiDarkGray.600">Aucun détail n’a été saisi avec la demande.</Text>
        )}
      </QuoteNotice>
    );
  },
);

export const ExpectedQuoteNotice = React.forwardRef(({ action, highlighted, quoteUpload }, ref) => {
  const reference = quoteReference(action);
  const requestedOn = noticeDate(action.since);

  return (
    <QuoteNotice
      ref={ref}
      testId="expected-quote"
      title={reference ? `Devis attendu : ${reference}` : 'Devis attendu'}
      dateLine={requestedOn && `Demandé le ${requestedOn}`}
      highlighted={highlighted}
      quoteUpload={quoteUpload}
    />
  );
});

export const Funders = ({
  folderId,
  trigger,
  quote,
  canEditVisit = false,
  onVisitUpdated,
  onQuoteUploaded,
  onCommentsUpdated,
  onDocumentsUpdated,
}) => {
  const dispatch = useDispatch();
  const authentication = useSelector(state => state.authentication || {});
  const { isOpen, onOpen, onClose } = useDisclosure();

  const {
    isOpen: isQuestionOpen,
    onOpen: onQuestionOpen,
    onClose: onQuestionClose,
  } = useDisclosure();
  const questionInputRef = useRef();
  const [folder, setFolder] = useState({});
  const [folderDetailsPending, setFolderDetailsPending] = useState(false);
  const [folderDetailsError, setFolderDetailsError] = useState(false);
  const [visitAt, setVisitAt] = useState(quote?.visitAt || '');
  const [visitPending, setVisitPending] = useState(false);
  const [projectDates, setProjectDates] = useState({
    projectStartedAt: quote?.projectStartedAt || '',
    projectEndedAt: quote?.projectEndedAt || '',
  });
  const [projectDatesPending, setProjectDatesPending] = useState(false);
  const [selectedStage, setSelectedStage] = useState(quote?.displayStage || '');
  const [focusedAction, setFocusedAction] = useState(null);
  const quoteNoticeRef = useRef(null);
  const quoteAction = quote?.quoteFollowUp?.actions?.find(
    action =>
      action.key === 'quote_required' ||
      action.modificationRequest ||
      QUOTE_ACTION_LABELS.includes(action.label),
  );
  const isModificationAction =
    Boolean(quoteAction?.modificationRequest) || quoteAction?.label === 'Devis modifié à déposer';
  const modificationRequest = isModificationAction
    ? quoteAction.modificationRequest || {
        quoteNumber: quoteAction.quoteNumber,
        requestedOn: quoteAction.since,
      }
    : null;
  const [quoteTickets, setQuoteTickets] = useState(null);
  const [historyPending, setHistoryPending] = useState(false);
  const [historyError, setHistoryError] = useState(false);
  const [ticketQuestion, setTicketQuestion] = useState('');
  const [ticketPending, setTicketPending] = useState(false);
  const [ticketError, setTicketError] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const selectedTicketTriggerRef = useRef(null);
  const fundersTriggerRef = useRef(null);
  const openedWithPointerRef = useRef(false);
  const historyButtonRef = useRef(null);
  const historyBackButtonRef = useRef(null);
  const folderDetailsContentRef = useRef(null);
  const folderDetailsRetryRef = useRef(null);
  const restoreFolderDetailsFocusRef = useRef(false);
  const [fundingDocuments, setFundingDocuments] = useState([]);
  const [fundingDocumentsPending, setFundingDocumentsPending] = useState(false);
  const [fundingDocumentsError, setFundingDocumentsError] = useState(false);
  const [fundingDocumentUploadError, setFundingDocumentUploadError] = useState(null);
  const [fundingDocumentUploadKey, setFundingDocumentUploadKey] = useState(null);
  const [fundingDocumentUploadStatus, setFundingDocumentUploadStatus] = useState(null);
  const fundingDocumentUploadInProgress = useRef(false);
  const [fundingDocumentReviewPendingId, setFundingDocumentReviewPendingId] = useState(null);
  const [fundingDocumentReviewError, setFundingDocumentReviewError] = useState(null);
  const fundingDocumentReviewInProgress = useRef(false);
  const [companyDocumentsCompletion, setCompanyDocumentsCompletion] = useState(null);
  const {
    isOpen: isFundingDocumentsOpen,
    onOpen: onFundingDocumentsOpen,
    onClose: onFundingDocumentsClose,
  } = useDisclosure();
  const {
    isOpen: isCompanyDocumentsOpen,
    onOpen: onCompanyDocumentsOpen,
    onClose: onCompanyDocumentsClose,
  } = useDisclosure();
  const { isOpen: isHistoryOpen, onOpen: onHistoryOpen, onClose: onHistoryClose } = useDisclosure();
  const currentUser =
    Object.keys(authentication.profile || {}).length > 0
      ? authentication.profile
      : authentication.user || {};
  const canAskQuestion = canAskQuestions(currentUser);
  const documentAccessRoles = [
    'administrateur',
    'ergothérapeute',
    'conseiller social',
    'coordinateur entreprises',
    'référent entreprise',
  ];
  const canAccessDocuments = documentAccessRoles.includes(currentUser.role);
  const canReviewFundingDocuments =
    ['administrateur', 'conseiller social'].includes(currentUser.role) ||
    (currentUser.role === 'ergothérapeute' &&
      currentUser.ergoQualification === 'ergothérapeute interne');
  const canWriteCompanyDocuments = [
    'administrateur',
    'référent entreprise',
    'coordinateur entreprises',
  ].includes(currentUser.role);
  const canUploadFundingDocuments =
    canWriteCompanyDocuments ||
    currentUser.role === 'conseiller social' ||
    (currentUser.role === 'ergothérapeute' &&
      currentUser.ergoQualification === 'ergothérapeute interne');
  const canUploadQuote =
    [
      'administrateur',
      'conseiller social',
      'référent entreprise',
      'coordinateur entreprises',
    ].includes(currentUser.role) ||
    (currentUser.role === 'ergothérapeute' &&
      currentUser.ergoQualification === 'ergothérapeute interne');
  const loadCompanyDocuments = () => {
    setCompanyDocumentsCompletion(null);
    return apiV1
      .get(`/companies_quote_groups/${quote.id}/documents`)
      .then(({ data }) => {
        if (!Array.isArray(data.documents)) throw new Error('Documents indisponibles');
        setCompanyDocumentsCompletion(getCompanyDocumentsCompletion(data.documents));
      })
      .catch(() =>
        setCompanyDocumentsCompletion({
          state: 'unavailable',
          description: 'État des documents entreprise indisponible.',
        }),
      );
  };
  const companyQuoteUpload = quote ? (
    <QuoteUpload
      folder={{
        formattedId: quote.folderFormattedId,
        recipientFullName: quote.recipientFullName,
      }}
      quote={quote}
      quoteRequest={{
        id: quote.id,
        quoteGroupId: quote.quoteGroupId,
        companyId: quote.companyId,
        companyName: quote.companyName,
        indicator: quote.indicator,
      }}
      successCallback={() => {
        if (canAccessDocuments) loadCompanyDocuments();
        if (onQuoteUploaded) onQuoteUploaded();
      }}
    />
  ) : null;

  const noticeQuoteUpload = triggerLabel =>
    canUploadQuote && companyQuoteUpload
      ? React.cloneElement(companyQuoteUpload, { triggerVariant: 'button', triggerLabel })
      : null;

  useEffect(() => {
    setVisitAt(quote?.visitAt || '');
    setProjectDates({
      projectStartedAt: quote?.projectStartedAt || '',
      projectEndedAt: quote?.projectEndedAt || '',
    });
    setSelectedStage(quote?.displayStage || '');
  }, [quote]);

  const folderFormattedId = folder?.folderFormattedId || folder?.formattedId;
  const recipientFullName = folder?.recipientLastName ? (
    <Text mt={2} as="small">
      <Text as="span" fontWeight="semibold" textTransform="uppercase" mr={1}>
        {folder?.recipientLastName || '-'}
      </Text>
      {folder?.recipientFirstName || '-'}
    </Text>
  ) : (
    <Text mt={2} as="small">
      {folder?.recipientFullName || '-'}
    </Text>
  );

  const recipientHousingCity = folder?.recipientCity || folder?.recipientHousingCity;
  const folderTag = folder?.tag || folder?.folderTag;
  const currentStage = quote?.displayStage;
  const requestedStage = selectedStage || currentStage;
  const visibleStage =
    quote?.statusColor === 'black' && DISCARDED_DISABLED_STAGES.includes(requestedStage)
      ? QUOTE_PROGRESS_STAGES.projectValidation
      : requestedStage;
  const hasTimeline = Boolean(currentStage && quote?.progressTotal);
  const showsStage = stage => !hasTimeline || visibleStage === stage;
  const loadFundingDocuments = () => {
    setFundingDocumentsPending(true);
    setFundingDocumentsError(false);
    return apiV1
      .get(`/folders/${folderId}/funding_documents`)
      .then(({ data }) => {
        const documents = Array.isArray(data) ? data : data?.documents;
        if (!Array.isArray(documents)) throw new Error('Documents indisponibles');
        setFundingDocuments(documents);
      })
      .catch(() => {
        setFundingDocuments([]);
        setFundingDocumentsError(true);
      })
      .finally(() => setFundingDocumentsPending(false));
  };

  const openFundingDocuments = () => {
    setFundingDocuments([]);
    setFundingDocumentUploadError(null);
    setFundingDocumentUploadStatus(null);
    setFundingDocumentReviewError(null);
    onFundingDocumentsOpen();
    loadFundingDocuments();
  };

  const openCompanyDocuments = () => {
    onCompanyDocumentsOpen();
  };

  const uploadFundingDocument = async (key, files) => {
    if (
      !files.length ||
      fundingDocumentUploadInProgress.current ||
      fundingDocumentReviewInProgress.current
    )
      return;
    fundingDocumentUploadInProgress.current = true;
    setFundingDocumentUploadError(null);
    setFundingDocumentUploadKey(key);

    try {
      const { uploaded, failures } = await uploadFundingDocumentFiles({
        folderId,
        key,
        files,
        onProgress: (position, total) =>
          setFundingDocumentUploadStatus({ key, message: uploadProgressMessage(position, total) }),
      });
      if (uploaded) await loadFundingDocuments();
      setFundingDocumentUploadStatus({
        key,
        message: uploadSummaryMessage(uploaded, files.length),
      });
      if (failures.length) {
        setFundingDocumentUploadError({ key, message: failures.join('\n') });
      }
    } finally {
      fundingDocumentUploadInProgress.current = false;
      setFundingDocumentUploadKey(null);
    }
  };

  const reviewFundingDocument = (document, state, reviewComment) => {
    if (fundingDocumentReviewInProgress.current || fundingDocumentUploadInProgress.current) return;
    fundingDocumentReviewInProgress.current = true;
    setFundingDocumentReviewPendingId(document.id || document.key);
    setFundingDocumentReviewError(null);
    apiV1
      .patch(`/folders/${folderId}/funding_documents/${document.id || document.key}`, {
        fundingDocument: {
          state,
          reviewVersion: document.reviewVersion,
          reviewComment: reviewComment || undefined,
        },
      })
      .then(() => loadFundingDocuments())
      .catch(reviewFailure => {
        setFundingDocumentReviewError(
          reviewFailure?.response?.data?.errors
            ? getUploadError(reviewFailure)
            : 'La validation n’a pas pu être enregistrée. Vous pouvez réessayer.',
        );
        return loadFundingDocuments();
      })
      .finally(() => {
        fundingDocumentReviewInProgress.current = false;
        setFundingDocumentReviewPendingId(null);
      });
  };

  const removeFundingDocumentFile = (document, file) => {
    if (fundingDocumentReviewInProgress.current || fundingDocumentUploadInProgress.current) return;
    fundingDocumentReviewInProgress.current = true;
    setFundingDocumentReviewPendingId(document.id || document.key);
    setFundingDocumentReviewError(null);
    apiV1
      .delete(`/folders/${folderId}/funding_documents/${document.id || document.key}/remove_file`, {
        data: { fundingDocument: { sharepointPath: file.sharepointPath } },
      })
      .then(() => loadFundingDocuments())
      .catch(removalFailure => {
        setFundingDocumentReviewError(
          removalFailure?.response?.data?.errors
            ? getUploadError(removalFailure)
            : 'Le fichier n’a pas pu être supprimé. Vous pouvez réessayer.',
        );
        return loadFundingDocuments();
      })
      .finally(() => {
        fundingDocumentReviewInProgress.current = false;
        setFundingDocumentReviewPendingId(null);
      });
  };

  const focusAfterRender = useCallback(ref => {
    setTimeout(() => {
      if (ref.current && typeof ref.current.focus === 'function') ref.current.focus();
    }, 0);
  }, []);

  const rememberTrigger = event => {
    openedWithPointerRef.current = Boolean(event?.detail);
    const triggerElement = event?.currentTarget || document.activeElement;
    if (triggerElement && triggerElement !== document.body) {
      fundersTriggerRef.current = triggerElement;
    }
  };

  const loadFolderDetails = ({ restoreFocus = false } = {}) => {
    if (folderDetailsPending) return undefined;
    if (restoreFocus) restoreFolderDetailsFocusRef.current = true;
    setFolderDetailsPending(true);
    setFolderDetailsError(false);
    return apiV1
      .get(`/folders/${folderId}/folder_details`)
      .then(folderResponse => {
        const folderData = folderResponse?.data?.folder;
        if (!folderData) throw new Error('Dossier indisponible');
        setFolder(folderData);
        if (canAccessDocuments) {
          loadFundingDocuments();
          if (quote?.id) loadCompanyDocuments();
        }
      })
      .catch(() => setFolderDetailsError(true))
      .finally(() => setFolderDetailsPending(false));
  };

  useEffect(() => {
    if (!restoreFolderDetailsFocusRef.current || folderDetailsPending) return undefined;
    if (!isOpen) {
      restoreFolderDetailsFocusRef.current = false;
      return undefined;
    }

    restoreFolderDetailsFocusRef.current = false;
    const target = folderDetailsError
      ? folderDetailsRetryRef.current
      : folderDetailsContentRef.current;
    if (!target || typeof target.focus !== 'function') return undefined;

    const timeoutId = setTimeout(() => target.focus(), 0);
    return () => clearTimeout(timeoutId);
  }, [folderDetailsError, folderDetailsPending, isOpen, folder]);

  const openFundersModal = (initialStage, event) => {
    rememberTrigger(event);
    setFocusedAction(null);
    setSelectedStage(initialStage || currentStage || '');
    setSelectedTicket(null);
    onHistoryClose();
    setFolder({});
    onOpen();
    loadFolderDetails({ restoreFocus: true });
  };

  const openRequiredAction = (action, event) => {
    rememberTrigger(event);
    if (action === 'Docs entreprise manquants') {
      openCompanyDocuments();
      return;
    }

    if (action === 'Docs admin manquants') {
      openFundingDocuments();
      return;
    }

    openFundersModal(QUOTE_PROGRESS_STAGES.projectValidation, event);
    setFocusedAction(action);
  };

  useEffect(() => {
    if (!isOpen || !focusedAction || folderDetailsPending || !quoteNoticeRef.current)
      return undefined;

    const timeoutId = setTimeout(() => {
      const notice = quoteNoticeRef.current;
      if (!notice) return;
      if (typeof notice.scrollIntoView === 'function') notice.scrollIntoView({ block: 'nearest' });
      notice.focus({ preventScroll: true });
    }, 0);
    return () => clearTimeout(timeoutId);
  }, [isOpen, focusedAction, folder, folderDetailsPending]);

  const loadQuoteTickets = () => {
    setHistoryPending(true);
    setHistoryError(false);
    return apiV1
      .get(`/folders/${folderId}/quote_tickets`)
      .then(({ data }) => setQuoteTickets(sortQuoteTickets(extractQuoteTickets(data))))
      .catch(() => {
        setQuoteTickets(null);
        setHistoryError(true);
      })
      .finally(() => setHistoryPending(false));
  };

  const openQuoteHistory = () => {
    onHistoryOpen();
    if (historyPending) return;

    loadQuoteTickets();
  };

  const closeQuoteHistory = useCallback(() => {
    setSelectedTicket(null);
    onHistoryClose();
    focusAfterRender(historyButtonRef);
  }, [focusAfterRender, onHistoryClose]);

  const selectQuoteTicket = (ticket, triggerElement) => {
    selectedTicketTriggerRef.current = triggerElement;
    setSelectedTicket(ticket);
  };

  const closeSelectedTicket = useCallback(() => {
    setSelectedTicket(null);
    focusAfterRender(selectedTicketTriggerRef);
  }, [focusAfterRender]);

  const closeFundersModal = useCallback(() => {
    setSelectedTicket(null);
    onHistoryClose();
    onClose();
    if (!openedWithPointerRef.current) focusAfterRender(fundersTriggerRef);
  }, [focusAfterRender, onClose, onHistoryClose]);

  useEffect(() => {
    if (!isHistoryOpen) return undefined;

    focusAfterRender(historyBackButtonRef);
    return undefined;
  }, [focusAfterRender, isHistoryOpen]);

  useEffect(() => {
    if (!isHistoryOpen) return undefined;

    const handleHistoryEscape = event => {
      if (event.key !== 'Escape' || selectedTicket) return;

      event.preventDefault();
      event.stopPropagation();
      closeFundersModal();
    };

    document.addEventListener('keydown', handleHistoryEscape, true);
    return () => document.removeEventListener('keydown', handleHistoryEscape, true);
  }, [closeFundersModal, isHistoryOpen, selectedTicket]);

  const createQuoteTicket = event => {
    event.preventDefault();
    const question = ticketQuestion.trim();
    if (!canAskQuestion || !question || ticketPending) return;

    setTicketPending(true);
    setTicketError(false);
    apiV1
      .post(`/folders/${folderId}/quote_tickets`, { quoteTicket: { question } })
      .then(() => {
        setTicketQuestion('');
        if (isQuestionOpen) {
          onQuestionClose();
          dispatch(toastActions.success('Votre question a été enregistrée.'));
        }
        return loadQuoteTickets();
      })
      .catch(() => setTicketError(true))
      .finally(() => setTicketPending(false));
  };

  const updateVisit = selectedVisitAt => {
    const formattedVisitAt = selectedVisitAt ? format(selectedVisitAt, 'yyyy-MM-dd') : '';

    setVisitPending(true);
    apiV1
      .patch(`/quote_request_summaries/${quote.id}/visit`, {
        quoteRequestSummary: { visitAt: formattedVisitAt },
      })
      .then(({ data }) => {
        const updatedQuote = data.quoteRequestSummary;
        setVisitAt(updatedQuote.visitAt || '');
        if (onVisitUpdated) onVisitUpdated(updatedQuote);
        dispatch(toastActions.success(TOAST_MESSAGE.success.visitUpdate));
      })
      .catch(() => dispatch(toastActions.error(TOAST_MESSAGE.error.visitUpdate)))
      .finally(() => setVisitPending(false));
  };

  const updateProjectDates = values => {
    const projectStartedAt = values.projectStartedAt
      ? format(values.projectStartedAt, 'yyyy-MM-dd')
      : '';
    const projectEndedAt = values.projectEndedAt ? format(values.projectEndedAt, 'yyyy-MM-dd') : '';

    setProjectDatesPending(true);
    apiV1
      .patch(`/quote_request_summaries/${quote.id}/project_dates`, {
        quoteRequestSummary: { projectStartedAt, projectEndedAt },
      })
      .then(({ data }) => {
        const updatedQuote = data.quoteRequestSummary;
        setProjectDates({
          projectStartedAt: updatedQuote.projectStartedAt || '',
          projectEndedAt: updatedQuote.projectEndedAt || '',
        });
        if (onVisitUpdated) onVisitUpdated(updatedQuote);
        dispatch(toastActions.success(TOAST_MESSAGE.success.projectDatesUpdate));
      })
      .catch(() => dispatch(toastActions.error(TOAST_MESSAGE.error.projectDatesUpdate)))
      .finally(() => setProjectDatesPending(false));
  };
  return (
    <>
      {trigger ? (
        trigger({
          onClick: event => openFundersModal(undefined, event),
          onActionClick: openRequiredAction,
          onAskQuestion: canAskQuestion ? onQuestionOpen : undefined,
        })
      ) : (
        <Tooltip
          bg="semiDarkGray.900"
          label="Détails dossier"
          placement="top"
          zIndex={1000}
          aria-label="Détails dossier"
        >
          <IconButton
            icon="info-outline"
            aria-label="Détails dossier"
            variant="ghost"
            variantColor={null}
            onClick={event => openFundersModal(undefined, event)}
            ref={fundersTriggerRef}
            color="coral.900"
            height={8}
            minW={8}
            rounded="full"
            _hover={{ color: 'white', backgroundColor: 'coral.900' }}
            _focus={{ shadow: 'none' }}
          />
        </Tooltip>
      )}

      <Modal
        isOpen={isQuestionOpen}
        onClose={ticketPending ? () => {} : onQuestionClose}
        initialFocusRef={questionInputRef}
        size="lg"
      >
        <ModalOverlay />
        <ModalContent rounded="lg" w="calc(100% - 24px)" maxW="32rem" mx="auto">
          <ModalHeader pr={12}>Poser une question</ModalHeader>
          <ModalCloseButton isDisabled={ticketPending} />
          <ModalBody pb={5}>
            <Text mb={4} fontSize="sm">
              Dossier n° {quote?.folderFormattedId || folderFormattedId || folderId}
              {quote?.recipientFullName ? ` — ${quote.recipientFullName}` : ''}
            </Text>
            <Box as="form" onSubmit={createQuoteTicket}>
              <Text as="label" htmlFor={`question-${folderId}`} display="block" mb={2}>
                Votre question à BelAvie
              </Text>
              <Textarea
                id={`question-${folderId}`}
                ref={questionInputRef}
                value={ticketQuestion}
                onChange={event => setTicketQuestion(event.target.value)}
                rows={5}
                isDisabled={ticketPending}
              />
              <Text mt={2} fontSize="xs" color="semiDarkGray.600">
                Vous retrouverez votre question dans l’historique du dossier.
              </Text>
              {ticketError && (
                <Text role="alert" mt={2} color="red.500">
                  La question n’a pas pu être envoyée. Votre texte est conservé.
                </Text>
              )}
              <Flex mt={4} justifyContent="flex-end" gridGap={3}>
                <Button
                  type="button"
                  variantColor="neutral"
                  onClick={onQuestionClose}
                  isDisabled={ticketPending}
                >
                  Annuler
                </Button>
                <Button
                  type="submit"
                  variantColor="neutral"
                  bg="coral.700"
                  color="white"
                  _hover={{ bg: 'semiDarkGray.900' }}
                  isLoading={ticketPending}
                  isDisabled={!ticketQuestion.trim()}
                >
                  Envoyer la question
                </Button>
              </Flex>
            </Box>
          </ModalBody>
        </ModalContent>
      </Modal>

      <Modal
        isOpen={isOpen}
        onClose={closeFundersModal}
        size="full"
        returnFocusOnClose={!openedWithPointerRef.current}
      >
        <ModalOverlay />
        <ModalContent ref={folderDetailsContentRef} rounded="lg" maxWidth="60rem" tabIndex={-1}>
          {folderDetailsPending && (
            <>
              <ModalHeader bg="darkGray" color="white" roundedTop="lg">
                Chargement du dossier
              </ModalHeader>
              <ModalCloseButton color="white" _focus={{ shadow: 'none' }} />
              <ModalBody pb={5}>
                <Flex alignItems="center" justifyContent="center" minH="8rem">
                  <Spinner aria-label="Chargement du dossier" />
                </Flex>
              </ModalBody>
            </>
          )}
          {!folderDetailsPending && folderDetailsError && (
            <>
              <ModalHeader bg="darkGray" color="white" roundedTop="lg">
                Dossier indisponible
              </ModalHeader>
              <ModalCloseButton color="white" _focus={{ shadow: 'none' }} />
              <ModalBody pb={5}>
                <Text role="alert" color="red.500" mb={4}>
                  Le dossier n’a pas pu être chargé.
                </Text>
                <Button
                  ref={folderDetailsRetryRef}
                  type="button"
                  onClick={() => loadFolderDetails({ restoreFocus: true })}
                  isLoading={folderDetailsPending}
                >
                  Réessayer
                </Button>
              </ModalBody>
            </>
          )}
          {!folderDetailsPending && !folderDetailsError && isHistoryOpen && (
            <QuoteHistoryView
              folderFormattedId={folderFormattedId}
              historyPending={historyPending}
              historyError={historyError}
              quoteTickets={quoteTickets}
              ticketQuestion={ticketQuestion}
              ticketPending={ticketPending}
              ticketError={ticketError}
              canAskQuestion={canAskQuestion}
              onBack={closeQuoteHistory}
              backButtonRef={historyBackButtonRef}
              onSelectTicket={selectQuoteTicket}
              onQuestionChange={event => setTicketQuestion(event.target.value)}
              onSubmit={createQuoteTicket}
            />
          )}
          {!folderDetailsPending && !folderDetailsError && !isHistoryOpen && (
            <>
              <ModalHeader as="header" bg="darkGray" color="white" fontSize="md" roundedTop="lg">
                <Heading as="h2" fontSize="xl" fontWeight="bold">
                  <Text
                    as="span"
                    fontFamily="heading"
                    opacity="0.5"
                    textTransform="uppercase"
                    fontWeight="600"
                    mr={1}
                  >
                    Dossier n°
                  </Text>
                  {folderFormattedId}
                  <Text as="span" d="inline-block" mx={2} opacity="0.65">
                    -
                  </Text>
                  {recipientFullName}
                </Heading>
                <Text mt={2}>
                  <Text as="span" fontWeight="bold" opacity="0.5" mr={1}>
                    Contact:
                  </Text>
                  <Text as="span">
                    {folder.contactDisplayName}
                    {folder.contactPhoneNumber && ' - '}
                    {folder.contactPhoneNumber}
                    {folder.contactEmail && ' - '}
                    {folder.contactEmail}
                  </Text>
                </Text>
                <Text mt={2}>
                  <Text as="span" fontWeight="bold" opacity="0.5" mr={1}>
                    Logement à adapter:
                  </Text>
                  <Text as="span">{folder.housingFullAddress || '-'}</Text>
                </Text>
                <Text mt={2}>
                  <Text as="span">{recipientHousingCity || '-'}</Text>
                  <Text as="span" d="inline-block" opacity="0.65">
                    &nbsp;/&nbsp;
                  </Text>
                  <Text as="span" textTransform="uppercase">
                    {folderTag || '-'}
                  </Text>
                </Text>
                <Flex mt={4} pr={8} flexWrap="wrap" gridGap={2}>
                  {canAskQuestion && (
                    <Button
                      type="button"
                      size="sm"
                      variantColor="neutral"
                      bg="coral.700"
                      color="white"
                      _hover={{ bg: 'white', color: 'darkGray' }}
                      onClick={onQuestionOpen}
                    >
                      Poser une question
                    </Button>
                  )}
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    variantColor="neutral"
                    color="white"
                    borderColor="rgba(255, 255, 255, 0.65)"
                    ref={historyButtonRef}
                    _hover={{ bg: 'white', color: 'darkGray' }}
                    _active={{ bg: 'white', color: 'darkGray' }}
                    onClick={openQuoteHistory}
                  >
                    Historique
                  </Button>
                  {canAccessDocuments && (
                    <DocumentSectionButton
                      label="Docs admin"
                      completion={getFundingDocumentsCompletion(fundingDocuments, {
                        pending: fundingDocumentsPending,
                        error: fundingDocumentsError,
                      })}
                      onClick={openFundingDocuments}
                    />
                  )}
                  {canAccessDocuments && quote?.id && (
                    <DocumentSectionButton
                      label="Documents de l’entreprise"
                      completion={companyDocumentsCompletion}
                      onClick={openCompanyDocuments}
                    />
                  )}
                </Flex>
              </ModalHeader>
              <ModalCloseButton color="white" _focus={{ shadow: 'none' }} />
              <ModalBody pb={5}>
                {modificationRequest && (
                  <ModificationRequestNotice
                    ref={quoteNoticeRef}
                    request={modificationRequest}
                    highlighted={Boolean(focusedAction)}
                    quoteUpload={noticeQuoteUpload('Déposer le devis modifié')}
                  />
                )}
                {quoteAction && !modificationRequest && (
                  <ExpectedQuoteNotice
                    ref={quoteNoticeRef}
                    action={quoteAction}
                    highlighted={Boolean(focusedAction)}
                    quoteUpload={noticeQuoteUpload('Déposer le devis')}
                  />
                )}
                {hasTimeline && (
                  <QuoteProgressTimeline
                    currentStage={currentStage}
                    progressCurrent={quote.progressCurrent}
                    progressTotal={quote.progressTotal}
                    selectedStage={visibleStage}
                    statusColor={quote.statusColor}
                    disabledStages={
                      quote.statusColor === 'black' ? DISCARDED_DISABLED_STAGES : undefined
                    }
                    stageMetadata={stageMetadata(folder, quote)}
                    detailsId="quote-stage-details"
                    onSelect={setSelectedStage}
                  />
                )}
                {quote && (
                  <Section mt={5} mb={5}>
                    <Heading as="h3" fontSize="md" mb={3}>
                      Actions du devis
                    </Heading>
                    <Flex alignItems="center">
                      <QuoteDocumentsAction
                        lastQuoteUploaded={quote.lastQuoteUploaded}
                        ergoReport={quote.ergoReport}
                      />
                      {canUploadQuote && companyQuoteUpload}
                      <Can
                        perform="comments:read"
                        yes={() => (
                          <Comments
                            folder={{
                              formattedId: quote.folderFormattedId,
                              id: folderId,
                              state: quote.folderState,
                              recipientFirstName: quote.recipientFirstName,
                              recipientLastName: quote.recipientLastName,
                            }}
                            comments={quote.comments || []}
                            updateFallback={updatedFolder => {
                              if (onCommentsUpdated) onCommentsUpdated(updatedFolder);
                            }}
                          />
                        )}
                      />
                    </Flex>
                  </Section>
                )}
                {quote && (
                  <Box
                    my={4}
                    border="1px solid"
                    borderColor="lightGray3"
                    rounded="lg"
                    overflow="hidden"
                  >
                    <Flex
                      px={4}
                      py={3}
                      alignItems="center"
                      justifyContent="space-between"
                      flexWrap="wrap"
                      gridGap={2}
                    >
                      <Text fontSize="sm">
                        Statut du dossier :{' '}
                        <Text as="span" fontWeight="semibold">
                          {quote.statusLabel || quote.status}
                        </Text>
                      </Text>
                      <Text fontSize="sm" color="semiDarkGray.600">
                        Ancienneté dans ce statut :{' '}
                        <Text as="span" fontWeight="semibold">
                          {displayStatusAge(quote.statusAgeDays)}
                        </Text>
                      </Text>
                    </Flex>
                    <FollowUpDetails key={quote.id} folder={folder} quote={quote} />
                  </Box>
                )}
                <Box
                  id={hasTimeline ? 'quote-stage-details' : undefined}
                  role={hasTimeline ? 'region' : undefined}
                  aria-live={hasTimeline ? 'polite' : undefined}
                  aria-label={hasTimeline ? `Détails : ${visibleStage}` : undefined}
                >
                  {hasTimeline && (
                    <Heading as="h3" fontSize="lg" mt={2} mb={3} color="semiDarkGray.900">
                      {visibleStage}
                    </Heading>
                  )}
                  {quote && showsStage(QUOTE_PROGRESS_STAGES.projectValidation) && (
                    <Section mt={5}>
                      <DetailGrid>
                        <DetailRow label="Date de positionnement" value={quote.positionedAt} />
                        {canEditVisit ? (
                          <Formik
                            enableReinitialize
                            initialValues={{ visitAt: visitAt ? parseISO(visitAt) : null }}
                            onSubmit={values => updateVisit(values.visitAt)}
                          >
                            {formik => (
                              <Flex
                                as="form"
                                gridColumn="1 / -1"
                                px={3}
                                py={2}
                                alignItems="flex-end"
                                flexWrap="wrap"
                                gridGap={3}
                                onSubmit={formik.handleSubmit}
                              >
                                <DateInput
                                  label="Visite de l’entreprise pour le devis :"
                                  id={`quote-visit-${quote.id}`}
                                  name="visitAt"
                                  enabledFutureDates
                                  width="18rem"
                                  style={{ mb: 0 }}
                                />
                                <Button
                                  type="submit"
                                  ml={3}
                                  variantColor="neutral"
                                  size="sm"
                                  bg="coral.900"
                                  color="white"
                                  _hover={{ bg: 'semiDarkGray.900' }}
                                  isLoading={visitPending}
                                >
                                  Enregistrer
                                </Button>
                              </Flex>
                            )}
                          </Formik>
                        ) : (
                          <DetailRow
                            label="Visite de l’entreprise pour le devis"
                            value={displayProjectDate(quote.visitAt)}
                          />
                        )}
                        <DetailRow
                          label="Référent sur le dossier"
                          value={folder.qualityController}
                        />
                        <DetailRow
                          label="Date du contrôle qualité"
                          value={folder.controlled_2dpeAt}
                        />
                        <DetailRow
                          label="Date de réception du dernier devis"
                          value={quote.lastQuoteReceivedAt}
                        />
                        <DetailRow
                          label="Date de validation des devis"
                          value={quote.lastQuoteValidatedAt}
                        />
                        <DetailRow
                          label="Date de fin d’analyse des devis"
                          value={folder.analysisFinishedAt}
                        />
                        <DetailRow
                          label="État actuel"
                          value={quoteCurrentState(quote, folder.analysisFinishedAt)}
                        />
                        {quote.quoteFollowUp?.otherCompaniesCount > 0 && (
                          <DetailRow
                            label="Autres devis sur ce dossier"
                            value={`Devis en cours avec ${
                              quote.quoteFollowUp.otherCompaniesCount
                            } autre${
                              quote.quoteFollowUp.otherCompaniesCount > 1 ? 's' : ''
                            } entreprise${quote.quoteFollowUp.otherCompaniesCount > 1 ? 's' : ''}`}
                          />
                        )}
                        <DetailRow
                          label="Date d’envoi au bénéficiaire"
                          value={folder.sentToRecipientAt}
                        />
                        <DetailRow
                          label="Date de réception de confirmation du choix du bénéficiaire"
                          value={folder.confirmationFromRecipientReceivedAt}
                        />
                      </DetailGrid>
                    </Section>
                  )}
                  {showsStage(QUOTE_PROGRESS_STAGES.diagnostic) && (
                    <Section mt={5}>
                      <DetailGrid>
                        <DetailRow
                          label="Référent sur le dossier"
                          value={folder.qualityController}
                        />
                        <DetailRow
                          label="Modalité d’intervention"
                          value={folder.ergoInterventionType}
                        />
                        <DetailRow
                          label="Date de premier contact"
                          value={folder.recipientContactedAt}
                        />
                        <DetailRow
                          label="Date de visite à domicile"
                          value={folder.housingVisitedAt}
                        />
                        <DetailRow
                          label="Date de dernière version des préconisations"
                          value={folder.updated_2dpeAt || folder.primary_2dpeFinalizedAt}
                          action={
                            quote?.ergoReport?.downloadLink ? (
                              <Tooltip
                                label="Ouvrir la dernière version du compte-rendu"
                                aria-label="Ouvrir la dernière version du compte-rendu"
                              >
                                <IconButton
                                  as="a"
                                  variantColor="neutral"
                                  href={quote.ergoReport.downloadLink}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  icon="view"
                                  size="sm"
                                  variant="ghost"
                                  aria-label="Ouvrir la dernière version du compte-rendu"
                                  color="semiDarkGray.900"
                                  _hover={{ bg: 'lightGray3' }}
                                  _active={{ bg: 'gray2' }}
                                />
                              </Tooltip>
                            ) : null
                          }
                        />
                      </DetailGrid>
                    </Section>
                  )}
                  {showsStage(QUOTE_PROGRESS_STAGES.funding) && (
                    <Section mt={5}>
                      <DetailGrid>
                        <DetailRow label="Conseiller·ère social" value={folder.socialAdviser} />
                        {folder.folderType === 'AMOc' && (
                          <DetailRow
                            label="Date d’envoi du plan de financement"
                            value={folder.fundingPlanSentAt}
                          />
                        )}
                        <DetailRow
                          label="Coût total des travaux"
                          value={worksAmount(folder.worksTotalPrice)}
                        />
                        <DetailRow
                          label="Coût des préconisations (hors travaux)"
                          value={folder.projectTotalPrice}
                        />
                      </DetailGrid>
                    </Section>
                  )}
                  {showsStage(QUOTE_PROGRESS_STAGES.funding) && (
                    <Section mt={5}>
                      <TabTableWrap px="0.5rem">
                        {Object.keys(folder.eligibleFundersMonitoring || {}).length ? (
                          <Table>
                            <thead>
                              <TabTBodyRow>
                                <TabTBodyTH
                                  w="300px"
                                  borderRight="0"
                                  textAlign="left"
                                  fontWeight="bold"
                                >
                                  Financeur
                                </TabTBodyTH>
                                <TabTBodyTH
                                  w="300px"
                                  borderRight="0"
                                  textAlign="left"
                                  fontWeight="bold"
                                >
                                  Montant accordé
                                </TabTBodyTH>
                                <TabTBodyTH
                                  w="300px"
                                  borderRight="0"
                                  textAlign="left"
                                  fontWeight="bold"
                                >
                                  Dépôt effectué le :
                                </TabTBodyTH>
                                <TabTBodyTH
                                  w="300px"
                                  borderRight="0"
                                  textAlign="left"
                                  fontWeight="bold"
                                >
                                  Date de retour
                                </TabTBodyTH>
                              </TabTBodyRow>
                            </thead>
                            <tbody>
                              {Object.values(folder.eligibleFundersMonitoring).map(funder => (
                                <TabTBodyRow key={funder.label}>
                                  <TabTBodyTH
                                    w="300px"
                                    borderRight="0"
                                    textAlign="left"
                                    fontWeight="normal"
                                  >
                                    {funder.label}
                                  </TabTBodyTH>
                                  <TabTD color="inherit" textAlign="left" fontWeight="normal">
                                    {/* eslint-disable-next-line no-nested-ternary */}
                                    {funder.isGrantedAmountKnown &&
                                    typeof funder.grantedAmount === 'number'
                                      ? `${funder.grantedAmount}€`
                                      : funder.fundingResponse === 'not_requested'
                                      ? 'Non-sollicité après accord du bénéficiaire'
                                      : 'Montant non connu'}
                                  </TabTD>
                                  <TabTD color="inherit" textAlign="left" fontWeight="normal">
                                    {funder.depositValidatedAt || '-'}
                                  </TabTD>
                                  <TabTD color="inherit" textAlign="left" fontWeight="normal">
                                    {funder.funderResponseReceivedAt || '-'}
                                  </TabTD>
                                </TabTBodyRow>
                              ))}
                            </tbody>
                          </Table>
                        ) : (
                          <Text textAlign="left" pb={2} pl={4}>
                            Aucun financeur disponible
                          </Text>
                        )}
                      </TabTableWrap>
                      <DetailGrid mt={3}>
                        <DetailRow
                          label="Fin d’instruction financière"
                          value={folder.financialInvestigationEndDate}
                        />
                      </DetailGrid>
                    </Section>
                  )}
                  {hasTimeline && visibleStage === QUOTE_PROGRESS_STAGES.projectFollowUp && (
                    <Section mt={5}>
                      <DetailGrid>
                        {canEditVisit &&
                        quote.status === 'Actif' &&
                        !folder.paidInvoicesReceivedAt &&
                        currentStage === QUOTE_PROGRESS_STAGES.projectFollowUp ? (
                          <Formik
                            enableReinitialize
                            initialValues={{
                              projectStartedAt: projectDateValue(projectDates.projectStartedAt),
                              projectEndedAt: projectDateValue(projectDates.projectEndedAt),
                            }}
                            onSubmit={updateProjectDates}
                          >
                            {formik => (
                              <Flex
                                as="form"
                                gridColumn="1 / -1"
                                px={3}
                                py={3}
                                alignItems="flex-end"
                                flexWrap="wrap"
                                gridGap={3}
                                onSubmit={formik.handleSubmit}
                              >
                                <DateInput
                                  label="Date de début de projet"
                                  id={`project-start-${quote.id}`}
                                  name="projectStartedAt"
                                  enabledFutureDates
                                  width="12rem"
                                  style={{ mb: 0 }}
                                />
                                <DateInput
                                  label="Date de fin de projet"
                                  id={`project-end-${quote.id}`}
                                  name="projectEndedAt"
                                  enabledFutureDates
                                  width="12rem"
                                  style={{ mb: 0 }}
                                />
                                <Button
                                  type="submit"
                                  size="sm"
                                  variantColor="neutral"
                                  bg="coral.900"
                                  color="white"
                                  _hover={{ bg: 'semiDarkGray.900' }}
                                  isLoading={projectDatesPending}
                                  isDisabled={!formik.dirty || projectDatesPending}
                                >
                                  Enregistrer les dates
                                </Button>
                              </Flex>
                            )}
                          </Formik>
                        ) : (
                          <>
                            <DetailRow
                              label="Date de début de projet"
                              value={displayProjectDate(projectDates.projectStartedAt)}
                            />
                            <DetailRow
                              label="Date de fin de projet"
                              value={displayProjectDate(projectDates.projectEndedAt)}
                            />
                          </>
                        )}
                        <DetailRow
                          label="Réception des pièces de déblocage des fonds"
                          value={folder.paidInvoicesReceivedAt}
                        />
                        <DetailRow
                          label="Bilan de fin de projet"
                          value={folder.projectEndReviewDate}
                        />
                        <DetailRow
                          label="Résultat du bilan de fin de projet"
                          value={folder.touchUp}
                        />
                        <DetailRow
                          label="Demande de déblocage des fonds"
                          value={folder.fundReleaseRequestedAt}
                        />
                        <DetailRow
                          label="Date de déblocage des fonds"
                          value={folder.fundsReleasedAt}
                        />
                      </DetailGrid>
                    </Section>
                  )}
                </Box>
              </ModalBody>
            </>
          )}
        </ModalContent>
      </Modal>
      {selectedTicket && (
        <Modal isOpen onClose={closeSelectedTicket} size="full">
          <ModalOverlay />
          <ModalContent rounded="16px" maxWidth="52rem" bg="#F9FAFB">
            <ModalHeader
              borderBottom="1px solid"
              borderColor="lightGray3"
              bg="white"
              roundedTop="16px"
              fontSize="md"
              py={5}
            >
              Détail de l’échange
            </ModalHeader>
            <ModalCloseButton top={4} />
            <ModalBody p={{ base: 5, md: 8 }}>
              <QuoteQuestionDetails
                key={selectedTicket.id}
                question={selectedTicket}
                folderId={folderId}
                onChange={updatedTicket => {
                  setSelectedTicket(updatedTicket);
                  setQuoteTickets(tickets =>
                    tickets?.map(ticket =>
                      ticket.id === updatedTicket.id ? updatedTicket : ticket,
                    ),
                  );
                }}
              />
            </ModalBody>
          </ModalContent>
        </Modal>
      )}
      <FundingDocumentsDrawer
        isOpen={isFundingDocumentsOpen}
        returnFocusOnClose={isOpen || !openedWithPointerRef.current}
        onClose={onFundingDocumentsClose}
        actionDetail={quote?.quoteFollowUp?.actions?.find(
          action => action.key === 'administrative_documents_missing',
        )}
        folderId={folderId}
        documents={fundingDocuments}
        pending={fundingDocumentsPending}
        error={fundingDocumentsError}
        uploadPendingKey={fundingDocumentUploadKey}
        uploadError={fundingDocumentUploadError}
        uploadStatus={fundingDocumentUploadStatus}
        canUpload={canUploadFundingDocuments}
        canReview={canReviewFundingDocuments}
        onRemoveFile={removeFundingDocumentFile}
        onUpload={uploadFundingDocument}
        onReview={reviewFundingDocument}
        reviewPendingId={fundingDocumentReviewPendingId}
        reviewError={fundingDocumentReviewError}
      />
      {quote?.id && (
        <CompanyDocumentsDrawer
          isOpen={isCompanyDocumentsOpen}
          returnFocusOnClose={isOpen || !openedWithPointerRef.current}
          onClose={() => {
            onCompanyDocumentsClose();
            if (onDocumentsUpdated) onDocumentsUpdated();
          }}
          quoteId={quote.id}
          actionDetail={quote?.quoteFollowUp?.actions?.find(
            action => action.key === 'company_documents_missing',
          )}
          canWrite={canUploadFundingDocuments}
          canFillDischargeLetter={canWriteCompanyDocuments}
          folderId={folderId}
          canUploadSharedDocuments={canUploadFundingDocuments}
          quoteUpload={React.cloneElement(companyQuoteUpload, { triggerVariant: 'button' })}
          onCompletionChange={setCompanyDocumentsCompletion}
        />
      )}
    </>
  );
};

export { FundingDocumentsList };
